import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import { toast } from "react-toastify";
import Api from "../Config/ApiConfig";

export default function CustomMessage() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({
    headline: "",
    summary: "",
    fullText: "",
    images: [],
    isActive: true
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await Api.get('custom-message');
      
      if (response.data.success) {
        setMessages(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load custom messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.headline || !formData.summary || !formData.fullText) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const url = editingMessage
        ? `custom-message/${editingMessage._id}`
        : `custom-message`;
      
      const response = editingMessage 
        ? await Api.put(url, formData)
        : await Api.post(url, formData);

      if (response.data.success) {
        toast.success(editingMessage ? "Message updated successfully" : "Message created successfully");
        setShowModal(false);
        resetForm();
        fetchMessages();
      }
    } catch (error) {
      console.error("Error saving message:", error);
      toast.error(error.response?.data?.message || "Failed to save message");
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      const response = await Api.delete(`custom-message/${messageId}`);

      if (response.data.success) {
        toast.success("Message deleted successfully");
        fetchMessages();
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message");
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      headline: message.headline,
      summary: message.summary,
      fullText: message.fullText,
      images: message.images || [],
      isActive: message.isActive
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      headline: "",
      summary: "",
      fullText: "",
      images: [],
      isActive: true
    });
    setEditingMessage(null);
  };

  const handleImageAdd = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  return (
    <Layout>
      <ContentWrapper>
        <div className="container-fluid">
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <h2>Custom Message Management</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <i className="fas fa-plus me-2"></i>
                  Create New Message
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row">
              {messages.length === 0 ? (
                <div className="col-12">
                  <div className="alert alert-info">
                    No custom messages found. Create one to get started!
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div key={message._id} className="col-md-6 col-lg-4 mb-4">
                    <div className={`card h-100 ${message.isActive ? 'border-success' : ''}`}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="card-title">{message.headline}</h5>
                          {message.isActive && (
                            <span className="badge bg-success">Active</span>
                          )}
                        </div>
                        <p className="card-text text-muted">{message.summary}</p>
                        <div className="mt-3">
                          <small className="text-muted">
                            Created: {new Date(message.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>
                      <div className="card-footer bg-transparent">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(message)}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(message._id)}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingMessage ? "Edit Message" : "Create New Message"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Headline *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.headline}
                        onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Summary *</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Full Text *</label>
                      <textarea
                        className="form-control"
                        rows="6"
                        value={formData.fullText}
                        onChange={(e) => setFormData({ ...formData, fullText: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Images (Optional)</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-block mb-2"
                        onClick={handleImageAdd}
                      >
                        <i className="fas fa-plus me-1"></i>
                        Add Image URL
                      </button>
                      {formData.images.map((url, index) => (
                        <div key={index} className="input-group mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={url}
                            readOnly
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleImageRemove(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="mb-3 form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Set as Active (Only one message can be active at a time)
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingMessage ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </ContentWrapper>
    </Layout>
  );
}
